import { useEffect, useMemo, useRef } from "react";
import tinycolor from 'tinycolor2';
import { useLoaderData } from "react-router-dom";
import { apiLoadingStatusSelector, customCssSelector, mapSelector, registerExternalFontFaceDeclarations, useAppDispatch, useAppSelector } from "../store";
import loadFontFacesFromStyleElement from "./Sidebar/EditSettings/EditStyle/loadFontFacesFromStyleElement";

export default function CustomCSS() {
  const dispatch = useAppDispatch();

  const apiResponse = useAppSelector(apiLoadingStatusSelector);
  const map = useAppSelector(mapSelector);
  const storeCustomCss = useAppSelector(customCssSelector);

  const loaderData = useLoaderData() as {
    primaryColor: string,
    secondaryColor: string,
    backgroundColor: string,
  };

  const primaryColor = loaderData.primaryColor ?? map.preferences.primaryColor ?? map.primaryColor;
  const secondaryColor = loaderData.secondaryColor ?? map.preferences.secondaryColor ?? map.secondaryColor;
  const darkModeDefault = '#1a1a2e';
  const backgroundColor = loaderData.backgroundColor ?? map.preferences.bgColor ?? (map.preferences.darkMode ? darkModeDefault : map.bgColor);

  const customCss = useMemo(
    () => {
      if (apiResponse !== 'success') return '';

      const brandColor = tinycolor(primaryColor);
      const secondary = tinycolor(secondaryColor);
      const background = tinycolor(backgroundColor || 'white');

      return `
:host, #visrez-interactive {
  --brand: ${brandColor.toHexString()};
  --brand-fg: ${brandColor.isDark() ? 'white' : 'black'};
  --secondary-color: ${secondaryColor};
  --secondary-fg: ${secondary.isDark() ? 'white' : 'black'};
  --secondary-hover: ${secondary.isDark() ? secondary.lighten(10).toHexString() : secondary.darken(10).toHexString()};
  --background-color: ${backgroundColor};
  --background-fg: ${background.isDark() ? 'white' : 'black'};
}
${storeCustomCss}
    `;
    },
    [apiResponse, primaryColor, secondaryColor, backgroundColor, storeCustomCss],
  );

  const el = useRef<HTMLStyleElement | null>(null);

  useEffect(() => {
    if (el.current && customCss !== '') {
      loadFontFacesFromStyleElement(el.current)
        .then((map) => {
          dispatch(registerExternalFontFaceDeclarations(map));
          return map;
        })
        .catch(() => null); // Don't show the error if it fails
    }
  }, [customCss, dispatch]);

  return (
    <style id="custom-css" ref={el}>
      {customCss}
    </style>
  );
}
